import type { OptionalRecord } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import type { RowAction } from '@festina-life/ui/listsAndTables'

type Actions = {
  onEdit?: () => void
  onDelete?: () => void
  onUndo?: () => void
}

/**
 * Edit, delete and undo row actions with consistent icons and titles.
 *
 * Only actions with a callback will be available (so actions can be omitted by not providing a callback).
 */
export const useRowActions = <T extends OptionalRecord>(
  rowActionsBuilder: (row: T) => Actions | undefined,
): ((row: T) => RowAction[]) => {
  const translate = useTranslate()

  return (row: T) => {
    const { onEdit, onDelete, onUndo } = rowActionsBuilder(row) ?? {}

    return [
      {
        key: 'edit',
        title: translate('edit'),
        iconName: 'Pencil' as const,
        onClick: onEdit,
      },
      {
        key: 'delete',
        title: translate('delete'),
        iconName: 'Trash' as const,
        onClick: onDelete,
      },
      {
        key: 'undo',
        title: translate('undo'),
        iconName: 'ArrowUturnDown' as const,
        onClick: onUndo,
      },
    ]
  }
}
